import React, { SyntheticEvent, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import {
  TableInstance,
  useFilters,
  useGlobalFilter,
  usePagination,
  UsePaginationInstanceProps,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import _get from 'lodash/get';
import { Theme } from '@app/theme';
import { RawCheckbox } from '@app/form';
import { ColumnOptions } from './DataTable';
import {
  arrayFilter,
  between,
  caseInsensitiveSort,
  countrySort,
  numberSort,
  startsWithFilter,
  tagsFilter,
  text,
} from './RowTypesMethods';
import { ColumnsFilters } from '.';
import { LocalizedCountry, useCountries } from 'queries';

interface Props<D extends object> {
  data: D[] | undefined;
  columns: ColumnOptions<D>[];
}

const useStyles = createUseStyles((theme: Theme) => ({
  label: {
    height: theme.marginBase * 5,
    width: theme.marginBase * 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const getColumnsWithDataOnly = <D extends object>(
  columns: Array<any>,
  data: D[],
) => {
  return columns.filter((col: any) => {
    const { accessor } = col;
    if (!accessor) {
      return true;
    }
    return data.some((p, index) => {
      if (typeof accessor === 'function') {
        return !!accessor(p, index, {} as any);
      }
      if (typeof accessor === 'string') {
        return !!_get(p, accessor);
      }
      return '';
    });
  });
};

const stopPropagation = (event: SyntheticEvent) => {
  event.stopPropagation();
};

const SelectionHeader = ({ getToggleAllPageRowsSelectedProps }: any) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  return (
    <div onClick={stopPropagation}>
      <RawCheckbox
        {...getToggleAllPageRowsSelectedProps()}
        labelClassName={classes.label}
      />
    </div>
  );
};

const SelectionCell = ({ row }: { row: any }) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  return (
    <div onClick={stopPropagation}>
      <RawCheckbox
        {...row.getToggleRowSelectedProps()}
        labelClassName={classes.label}
      />
    </div>
  );
};

const useSelectionColumn = (hooks: any) => {
  hooks.visibleColumns.push((columns: any) => [
    {
      id: 'selection',
      notOrderable: true,
      centered: true,
      disableFilters: true,
      disableGlobalFilter: true,
      Header: SelectionHeader,
      Cell: SelectionCell,
    } as any,
    ...columns,
  ]);
};

const getRowId = (_data: { id: string }) => _data.id;

const filterTypes = {
  startsWith: startsWithFilter,
  tags: tagsFilter,
  array: arrayFilter,
  text,
  between,
};

const emptyArray: any[] = [];
export const useDatatable = <D extends { id: string }>({
  data = emptyArray,
  columns,
}: Props<D>): any => {
  const { data: countries } = useCountries();

  const countriesByKey = useMemo(() => {
    if (!countries) {
      return {};
    }
    return countries.reduce((acc, country) => {
      acc[country.key] = country;
      return acc;
    }, {} as Record<string, LocalizedCountry>);
  }, [countries]);

  const sortTypes = useMemo(
    () => ({
      alphanumeric: caseInsensitiveSort,
      number: numberSort,
      country: countrySort(countriesByKey),
    }),
    [countriesByKey],
  );
  const options = useMemo(() => {
    return {
      columns: getColumnsWithDataOnly(columns, data),
      data,
      getRowId,
      sortTypes,
      filterTypes,
      autoResetPage: true,
      defaultColumn: {
        Filter: ColumnsFilters.TextFilter,
        filter: 'text',
      } as any,
    };
  }, [columns, data, sortTypes]);

  const tableInfos = useTable(
    options,
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    useSelectionColumn,
  ) as UsePaginationInstanceProps<D> & TableInstance<D>;

  const { selectedRowIds } = tableInfos.state as {
    selectedRowIds: { [key: string]: boolean };
  };
  const selectedRowIdsArray = useMemo(
    () => Object.keys(selectedRowIds).filter((id) => selectedRowIds[id]),
    [selectedRowIds],
  );

  // @ts-ignore
  tableInfos.selectedRowIds = selectedRowIdsArray;

  return tableInfos;
};
